import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { bindCacheInvalidation, aplicarConfigDeCache } from "../../services/cachedApi";
import { getCompanyExpiryStatus } from "../../services/companyExpiryService";
import moment from "moment";
import {
  devePedirRenovacao,
  deveEncerrarSessao, sairParaLogin } from "./politicaDeSessao";

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState(null);
  
  // Ref para manter referência dos listeners ativos
  const listenersRef = useRef(new Set());

  /**
   * Sessão acabou de verdade (refresh recusado, ou logout): limpa TUDO que
   * identifica o usuário e vai para o login.
   *
   * Antes o encerramento forçado só marcava `isAuth=false`: `user` e `socket`
   * continuavam no contexto, a tela de tickets seguia montada com o socket
   * derrubado, e o usuário ficava numa tela "logada" sem sessão até dar F5
   * (11-09-2026). O socket é desligado antes de sair do contexto para os
   * componentes verem `null` e limparem os listeners (ver a guarda deles).
   */
  const encerrarSessaoLocal = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("cshow");
    api.defaults.headers.Authorization = undefined;
    setSocket((atual) => {
      try {
        if (atual && typeof atual.disconnect === "function") atual.disconnect();
      } catch (e) {
        /* noop */
      }
      return null;
    });
    setIsAuth(false);
    setUser({});
    try {
      history.push("/login");
    } catch (e) {
      /* noop */
    }
  };

  /**
   * Interceptors do axios.
   *
   * Registrados DENTRO de um effect, com `eject` no cleanup. Antes ficavam no
   * corpo do hook: cada render adicionava um par novo, nenhum era removido, e
   * eles se acumulavam pela vida da página — dezenas de cópias do mesmo
   * interceptor refazendo o mesmo trabalho a cada request.
   */
  useEffect(() => {
    const noPedido = api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token) {
          config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
          setIsAuth(true);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const naResposta = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config;
        const status = error?.response?.status;

        // Renova e repete. Aceita 401 e 403: o backend responde 401
        // (ERR_SESSION_EXPIRED) quando o access token vence, e antes só o 403
        // era tratado — ou seja, a renovação automática nunca disparava para o
        // erro que de fato acontece.
        if (devePedirRenovacao(status, original)) {
          original._retry = true;
          try {
            const { data } = await api.post("/auth/refresh_token");
            if (data?.token) {
              localStorage.setItem("token", JSON.stringify(data.token));
              api.defaults.headers.Authorization = `Bearer ${data.token}`;
            }
            return api(original);
          } catch (falhaAoRenovar) {
            // Renovar falhou: aí sim a sessão acabou de verdade. Cai no
            // encerramento abaixo através do erro do próprio refresh.
            return Promise.reject(falhaAoRenovar);
          }
        }

        // ENCERRA a sessão só quando quem recusou foi a renovação.
        //
        // Antes, QUALQUER 401 apagava o token e deslogava. Como o app monta o
        // WhatsAppsProvider e o LoggedInLayout antes de o auth resolver (eles
        // ficam fora do isPrivate, em routes/index.js), esses componentes
        // disparam /settings, /whatsapp e afins com a sessão ainda em
        // construção. Um 401 desses derrubava a sessão que o login acabara de
        // criar — daí o "acabei de logar e deu erro", intermitente, decidido por
        // quem respondesse primeiro.
        if (deveEncerrarSessao(status, original)) {
          encerrarSessaoLocal();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(noPedido);
      api.interceptors.response.eject(naResposta);
    };
  }, []);

  // Effect para inicialização do token
  useEffect(() => {
    const token = localStorage.getItem("token");
    (async () => {
      if (token) {
        try {
          const { data } = await api.post("/auth/refresh_token");
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          setIsAuth(true);
          setUser(data.user || data);
        } catch (err) {
          toastError(err);
        }
      }
      setLoading(false);
    })();
  }, []);

  // Effect para configuração do socket
  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      console.log("Configurando socket para user", user.id, "company", user.companyId);
      
      // Limpar listeners anteriores
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }

      // Criar nova conexão socket
      const socketInstance = socketConnection({ user: {
        companyId: user.companyId,
        id: user.id }
      });
      
      if (socketInstance) {
        setSocket(socketInstance);

        // Aguardar um pouco para garantir que o socket está configurado
        setTimeout(() => {
          const eventName = `company-${user.companyId}-user`;
          
          const handleUserUpdate = (data) => {
            if (data.action === "update" && data.user.id === user.id) {
              setUser(data.user);
            }
          };

          // Verificar se o socket tem o método 'on'
          if (socketInstance && typeof socketInstance.on === 'function') {
            socketInstance.on(eventName, handleUserUpdate);
            listenersRef.current.add(eventName);
            // Mudança feita por outro usuário derruba o cache de leitura
            // (tags, configurações, plano) antes do TTL — services/cachedApi.js
            bindCacheInvalidation(socketInstance, user.companyId).forEach(ev =>
              listenersRef.current.add(ev)
            );
            // Interruptor do backend: se o suporte desligar o cache, isto é o
            // que faz a decisão chegar no navegador (sem deploy).
            aplicarConfigDeCache();
            console.log(`Listener adicionado para: ${eventName}`);
          } else {
            console.error("Socket instance não tem método 'on'", socketInstance);
          }
        }, 100);
      }
    }

    // Cleanup function
    return () => {
      if (socket && listenersRef.current.size > 0) {
        console.log("Limpando listeners do socket para user", user.id);
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }
    };
  }, [user.id, user.companyId]); // Dependências específicas

  // Effect para buscar dados do usuário atual
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user || data);
      } catch (err) {
        console.log("Erro ao buscar usuário atual:", err);
      }
    };
    
    if (isAuth) {
      fetchCurrentUser();
    }
  }, [isAuth]);

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);
      const {
        user: { company },
      } = data;

      // Lógica de configurações da empresa (mantém como estava)
      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        const signEnable = setting.value === "enable";

        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable);
        }
      }
      
      localStorage.setItem("profileImage", data.user.profileImage);

      moment.locale("pt-br");

      // Configurar token para próximas chamadas
      localStorage.setItem("token", JSON.stringify(data.token));
      api.defaults.headers.Authorization = `Bearer ${data.token}`;
      setUser(data.user || data);
      setIsAuth(true);

      // Verificar status de vencimento usando nova API
      try {
        const expiryResponse = await getCompanyExpiryStatus();
        const expiryStatus = expiryResponse.data;
        console.log('🎯 Nova lógica de vencimento:', expiryStatus);

        localStorage.setItem("companyDueDate", expiryStatus.dueDate);

        if (expiryStatus.status === "active") {
          // Plano ativo - permite login normalmente
          toast.success(i18n.t("auth.toasts.success"));

          // Aviso se vence em breve (5 dias ou menos)
          if (expiryStatus.shouldShowFinancialScreen) {
            toast.warn(
              `Sua assinatura vence em ${expiryStatus.daysUntilExpiry} ${
                expiryStatus.daysUntilExpiry === 1 ? "dia" : "dias"
              }`
            );
          }

          history.push("/tickets");
        } else if (expiryStatus.status === "expired_with_grace") {
          // Plano vencido mas dentro do período de tolerância
          toast.success(i18n.t("auth.toasts.success"));

          // Informar sobre os diferentes períodos de tolerância
          const daysUntilSystemBlock = expiryStatus.systemGracePeriodDays - expiryStatus.daysAfterExpiry;
          const daysUntilWhatsappDisconnect = expiryStatus.whatsappGracePeriodDays - expiryStatus.daysAfterExpiry;

          let message = `⚠️ Atenção: Sua assinatura venceu há ${expiryStatus.daysAfterExpiry} ${
            expiryStatus.daysAfterExpiry === 1 ? "dia" : "dias"
          }.`;

          if (daysUntilWhatsappDisconnect > 0) {
            message += ` WhatsApp será desconectado em ${daysUntilWhatsappDisconnect} ${
              daysUntilWhatsappDisconnect === 1 ? "dia" : "dias"
            }.`;
          } else {
            message += " WhatsApp foi desconectado.";
          }

          if (daysUntilSystemBlock > 0) {
            message += ` Sistema será bloqueado em ${daysUntilSystemBlock} ${
              daysUntilSystemBlock === 1 ? "dia" : "dias"
            }.`;
          }

          toast.warn(message, { autoClose: 10000 });

          // Redirecionar para tela de financeiro para pagamento
          history.push("/financeiro");
        } else {
          // Plano vencido e fora do período de tolerância - bloquear acesso
          toastError(
            `Sua assinatura venceu há ${expiryStatus.daysAfterExpiry} dias e excedeu o período de tolerância de ${expiryStatus.systemGracePeriodDays} dias para bloqueio do sistema. Entre em contato com o Suporte para mais informações!`
          );
          history.push("/financeiro-aberto");
        }
      } catch (expiryError) {
        // Fallback para lógica antiga em caso de erro na API
        console.error("❌ Erro ao verificar status de vencimento:", expiryError);
        console.log("🔄 Usando fallback para lógica antiga");

        let dueDate;
        if (data.user.company.id === 1) {
          dueDate = "2999-12-31T00:00:00.000Z";
        } else {
          dueDate = data.user.company.dueDate;
        }

        const vencimento = moment(dueDate).format("DD/MM/yyyy");
        const before = moment().isBefore(moment(dueDate));

        localStorage.setItem("companyDueDate", vencimento);

        if (before === true) {
          toast.success(i18n.t("auth.toasts.success"));
          history.push("/tickets");
        } else {
          toastError(`Sua assinatura venceu ${vencimento}. Entre em contato com o Suporte para mais informações!`);
          history.push("/financeiro-aberto");
        }
      }

      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      // Limpar socket antes do logout
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
        
        if (typeof socket.disconnect === 'function') {
          socket.disconnect();
        }
      }

      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      setSocket(null);
      localStorage.removeItem("token");
      localStorage.removeItem("cshow");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      // Recarrega: estado de plugin da sessão anterior não sobrevive na aba.
      sairParaLogin();
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      console.log(data);
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket,
  };
};

export default useAuth;